import {
  Avatar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { DeepPartial } from 'react-hook-form';
import Label from 'src/components/label';
import { PropertyModel } from 'src/gql/graphql';
import { fDateTime } from 'src/utils/format-time';
import {
  TableHeadCustom,
  TableSkeleton,
  TableEmptyRows,
  TablePaginationCustom,
  useTable,
} from 'src/components/table';
import TableNoData from 'src/components/table/table-no-data';
import { emptyRows } from 'src/components/table/utils';
import { compact } from 'lodash';
import Scrollbar from 'src/components/scrollbar';
import { PROPERTY_STATUS_LABEL_COLOR } from './data';

type PropertyTableListSmallProps<T = DeepPartial<PropertyModel>> = {
  table: ReturnType<typeof useTable>;
  items: T[];
  total?: number;
  isLoading: boolean;
  isNotFound?: boolean;
  action?: (item: T) => React.ReactNode;
  selected?: (id: string) => boolean;
  onSelect?: (item: T) => void;
};

export function PropertyTableListSmall({
  table,
  items,
  total,
  isLoading,
  isNotFound,
  action,
  selected,
  onSelect,
}: PropertyTableListSmallProps) {
  total = total ?? items.length;

  const denseHeight = table.dense ? 60 : 80;

  const handleSelect = (item: DeepPartial<PropertyModel>) => () =>
    onSelect?.(item);

  return (
    <TableContainer sx={{ overflow: 'unset' }}>
      <Scrollbar>
        <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
          <TableHeadCustom
            headLabel={[
              { id: '_id', label: 'ID', width: 80 },
              { id: 'name', label: 'Name', width: 240 },
              { id: 'location', label: 'Location' },
              { id: 'status.name', label: 'Status' },
              { id: 'createdAt', label: 'Created At' },
              ...(action
                ? [{ id: 'action', label: 'Action', width: 120 }]
                : []),
            ]}
            sx={{ position: 'sticky', top: 0, zIndex: 99 }}
          />

          <TableBody>
            {isLoading &&
              [...Array(table.rowsPerPage)].map((_, index) => (
                <TableSkeleton key={index} sx={{ height: denseHeight }} />
              ))}
            {items.map((item) => (
              <PropertyTableListRowSmall
                key={item.id}
                item={item}
                action={action?.(item)}
                selected={selected?.(item?.id!)}
                onClick={handleSelect(item)}
              />
            ))}

            <TableEmptyRows
              height={denseHeight}
              emptyRows={emptyRows(table.page, table.rowsPerPage, total)}
            />

            <TableNoData notFound={isNotFound ?? !total} sx={{ py: 3 }} />
          </TableBody>
        </Table>
      </Scrollbar>

      {items.length > 0 && (
        <TablePaginationCustom
          count={total}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          rowsPerPageOptions={[10, 30, 50, 100]}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 99,
            backgroundColor: 'background.paper',
          }}
        />
      )}
    </TableContainer>
  );
}

type PropertyTableListRowSmallProps = {
  item: DeepPartial<PropertyModel>;
  selected?: boolean;
  action?: React.ReactNode;
  onClick: VoidFunction;
};

export function PropertyTableListRowSmall({
  item,
  selected,
  action,
  onClick,
}: PropertyTableListRowSmallProps) {
  const emptyValue = (
    <Typography variant="inherit" sx={{ color: 'text.secondary' }}>
      N/A
    </Typography>
  );

  const locations = compact([
    item.location?.neighbourhood?.name,
    item.location?.area?.name,
    item.location?.municipality?.name,
  ]);

  return (
    <TableRow
      hover
      selected={selected}
      sx={{ cursor: 'pointer', position: 'relative', clipPath: 'inset(0)' }}
    >
      <TableCell>
        <Typography variant="inherit">{item.id}</Typography>

        <Stack sx={{ position: 'absolute', inset: 0 }} onClick={onClick} />
      </TableCell>
      <TableCell>
        <Stack flexDirection="row" alignItems="center" gap={2}>
          <Stack
            sx={{
              width: 40,
              height: 40,
              background: 'text.disabled',
              borderRadius: 8,
            }}
          >
            {!!item.cover && (
              <Avatar
                src={item.cover}
                variant="rounded"
                sx={{ width: 40, height: 40 }}
              />
            )}
          </Stack>

          <Stack width="100%" minWidth={0}>
            <Typography variant="body2" noWrap>
              {item.name || emptyValue}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary' }}
              noWrap
            >
              {item.resaleRef ?? item.parent?.resaleRef ?? emptyValue}
            </Typography>
          </Stack>
        </Stack>
      </TableCell>
      <TableCell>
        {locations.length > 0 ? (
          <Typography variant="body2" noWrap>
            {locations.join(', ')}
          </Typography>
        ) : (
          emptyValue
        )}
        {!!item.parent && (
          <Tooltip title={item.parent?.name}>
            <Typography
              variant="caption"
              noWrap
              sx={{ color: 'text.secondary' }}
            >
              {item.parent.id}. {item.parent.name || emptyValue}
            </Typography>
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        {item.status ? (
          <Label
            variant="soft"
            color={PROPERTY_STATUS_LABEL_COLOR[item.status.key!]}
          >
            {item.status.name}
          </Label>
        ) : (
          emptyValue
        )}
      </TableCell>
      <TableCell>
        <Typography noWrap variant="inherit">
          {fDateTime(item.createdAt)}
        </Typography>
      </TableCell>
      {!!action && <TableCell sx={{ width: 120 }}>{action}</TableCell>}
    </TableRow>
  );
}
